// EmmA in the header — a strip that alerts, and a drawer that gives her the width.
//
// Jacky, over three rounds, settling the shape:
//   · "chat with emma should be on header, drop down, pin, whatever design you propose, every
//     page I go, I should be able to call emma"
//   · "i dont like this emma dock, i rather drop down from top, so the width is not hogged, if
//     she has message to me, the top collapse box should alert me, i click to drop it down"
//   · "no emma on width pane please, she is top drawer drop down, need to talk to her in width"
//
// So: the strip is one line and always there; the drawer opens ACROSS the content, never beside
// it; and when she has something for him the strip is the thing that tells him.
//
// She is not a feature in here. There is no "she has no skill", no "register a backlog", and no
// approval chain around his own instruction — his instruction IS the approval, and only
// communication comes back with a button on it (AGENTS.md).
import {useEffect, useMemo, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {CaseCard, cn} from '@aiw/ui';
import {authHeaders} from '../lib/api';
import {useCases} from '../hooks/useCases';
import {useDeskDrafts} from '../hooks/useAttention';
import {supabase} from '../lib/auth';
import {useT} from '../lib/i18n';
import {communicationSummary,unreviewedDrafts} from '../lib/communication-summary';
import {AppError} from '../shell/AppError';
import {EmmaAvatar, EmmaPlanPose} from './emma-avatar';
import {
  parseChatFrame, pageContext, splitFrames, getEmmaSession, setEmmaSession, loadEmmaHistory, loadConversations, quickReplies,
  EmmaReadError, type Conversation, applyProgress, type ProgressLine} from '../lib/emma-chat';
import {
  ACCEPT, fmtSize, kindOf, listAttachments, notYetHanded, openAttachment, refusalFor, uploadAttachment,
  type Attachment, type TurnFile} from '../lib/emma-attachments';

/** `progress` is her process as a person reads it (one plain line per step); `steps` the tool code
 *  names and `reasoning` the model's own thinking, both kept for the "Show details" fold only. */
type Turn = {role: 'you' | 'emma'; text: string; steps?: string[]; progress?: ProgressLine[]; reasoning?: string[]; live?: boolean; proposals?: {id?: string; title?: string}[]; model?: string;
  /** ER-08: the files handed in with this message. The chip sits on the turn it came with. */
  files?: TurnFile[]};

const HISTORY_KEY = 'aiw.emma.history';

/** Her process, one plain line per step. Live, the open line pulses. */
function ProgressLines({lines, live}: {lines: ProgressLine[]; live?: boolean}) {
  return (
    <ol className="mb-1 space-y-0.5 text-micro" aria-live={live ? 'polite' : undefined}>
      {lines.map((l, i) => (
        <li key={i} className={cn('flex items-baseline gap-1.5', l.state === 'error' ? 'text-destructive' : l.state === 'doing' ? 'text-foreground' : 'text-muted-foreground')} title={l.detail ?? undefined}>
          <span className="inline-block w-3 text-center" aria-hidden>{l.state === 'doing' ? <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" /> : l.state === 'ok' ? '✓' : '✗'}</span>
          <span>{l.label}{l.state === 'error' && l.detail ? ` — ${l.detail}` : ''}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * A file on a turn, or waiting in the tray: its kind, name and size; a click reopens the exact revision
 * (ER-08). The × is only in the tray and only unmarks the file from the NEXT message — the file stays on
 * the conversation and she keeps reading it, because the Brain reads every attached file every turn.
 */
function FileChip({file, note, onOpen, onRemove, onYou}: {file: TurnFile; note?: string | null; onOpen: (f: TurnFile) => void; onRemove?: () => void; onYou?: boolean}) {
  const {t} = useT();
  const kind = t(`emma.attachKind.${kindOf(file.mime)}`);
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-micro', onYou ? 'border-primary-foreground/40 bg-primary-foreground/10' : 'bg-card')} title={note ?? t('emma.attachNote', {kind, size: fmtSize(file.size_bytes)})}>
      <button type="button" className="inline-flex min-w-0 items-center gap-1 text-left" onClick={() => onOpen(file)} aria-label={t('emma.attachOpen', {name: file.name})}>
        <span className={cn('shrink-0 font-mono uppercase', onYou ? 'opacity-80' : 'text-muted-foreground')}>{kind}</span>
        <span className="truncate underline-offset-2 hover:underline">{file.name}</span>
        <span className={cn('shrink-0 font-mono', onYou ? 'opacity-80' : 'text-muted-foreground')}>{fmtSize(file.size_bytes)}</span>
      </button>
      {onRemove && <button type="button" className="ml-0.5 shrink-0 px-1 text-muted-foreground hover:text-foreground" onClick={onRemove} aria-label={t('emma.attachRemove', {name: file.name})}>×</button>}
    </span>
  );
}

/**
 * The conversation list, remembered per browser so the drawer opens on the thread he was last in.
 *
 * A LIST, not one id. Before this the drawer held a single session in localStorage, so "the
 * conversation" was whichever UUID that browser happened to mint first and every other conversation he
 * had ever had — from the phone, the console, another browser — was invisible. Jacky's shape for it,
 * 2026-09-17: *"in the CRM drawer conversation is continous, and user can start a new conversation"*.
 */
function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}

function writeHistory(ids: string[]): void {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(ids.slice(0, 20))); } catch { /* private mode */ }
}

export function EmmaDrawer({openCases}: {openCases: number}) {
  const {t} = useT();
  const {pathname} = useLocation();
  const [dropped, setDropped] = useState(false);
  const [composePrompt,setComposePrompt]=useState('');
  useEffect(()=>{
    const compose=(event:Event)=>{const prompt=(event as CustomEvent<{prompt?:string}>).detail?.prompt;if(!prompt)return;setComposePrompt(prompt);setDropped(true);};
    window.addEventListener('aiw:emma-compose',compose);return()=>window.removeEventListener('aiw:emma-compose',compose);
  },[]);

  /**
   * GOING SOMEWHERE CLOSES HER. Jacky, opening the full desk with the drawer still expanded: *"look
   * full desk open but drawer still on top, its broken"* — and it is: the drawer is a drop-down over
   * the page, so leaving the page it was opened on must put it away. Without this it stayed across
   * every navigation, covering the desk that shows the same conversation, which reads as two
   * conversations stacked on each other.
   */
  useEffect(() => { setDropped(false); }, [pathname]);

  /**
   * ⌘J OPENS AND CLOSES HER; ESC CLOSES HER. The strip has advertised ⌘J since it was built and nothing
   * listened for it, so the only way out was the small word at the right-hand end of the strip. Jacky,
   * 12 Oct 2026: *"closing her down is a tiny right hand button"*.
   */
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') { e.preventDefault(); setDropped((d) => !d); }
      else if (e.key === 'Escape') setDropped(false);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);
  const {open} = useCases();
  const messages = useDeskDrafts();
  const prepared = unreviewedDrafts(messages.data??[],open);
  const waiting = open.length + prepared.length;
  const alerting = waiting > 0;
  const strip = alerting ? t('emma.stripWaiting', {count: waiting}) : t('emma.stripIdle');

  return (
    // SHE DROPS OVER THE PAGE, SHE DOES NOT PUSH IT. The drawer used to sit in the column's flow, so
    // opening her squeezed the page underneath into whatever height was left. Jacky, 12 Oct 2026: "bring
    // her down compress the main screen". The body is an overlay below the strip now; the page keeps its
    // size and a click on it (the dimmed backdrop) puts her away.
    <div className="relative z-40 border-b bg-card">
      <button
        type="button"
        onClick={() => setDropped((d) => !d)}
        aria-expanded={dropped}
        className={cn('flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm', alerting && 'bg-primary/5')}
      >
        <span className={cn('flex items-center gap-1.5 font-medium', alerting ? 'text-primary' : 'text-muted-foreground')}>
          <EmmaAvatar size={22} className={cn(alerting && 'ring-2 ring-primary/60')} />EmmA
        </span>
        <span className={cn('truncate', alerting ? 'text-foreground' : 'text-muted-foreground')}>{strip}</span>
        <span className="ml-auto flex items-center gap-2 font-mono text-micro text-muted-foreground">
          <kbd className="rounded border px-1">⌘J</kbd>
          <span>{dropped ? t('emma.close') : t('emma.open')}</span>
        </span>
      </button>
      {/* SHE IS CONTAINED, NOT THE PAGE. A render failure inside her used to take the whole CRM to a
          blank screen — measured with a deliberate throw in this very component: the rail, the page
          and everything else unmounted and left white, with nothing on screen to report. She is the
          one surface every page shares, so she is also the one most able to take the app down with
          her. The strip lives OUTSIDE this boundary on purpose: if the body throws, the strip is
          still there, the reason is on screen, and the work behind it is untouched. */}
      {dropped && (
        <>
          <div aria-hidden className="absolute inset-x-0 top-full h-screen bg-foreground/25" onClick={() => setDropped(false)} />
          <div className="absolute inset-x-0 top-full flex h-[min(78vh,820px)] flex-col border-b bg-card shadow-2xl">
            <AppError where="Her drawer">
              <EmmaDrawerBody openCases={openCases} dropped={dropped} onClose={() => setDropped(false)} composePrompt={composePrompt} />
            </AppError>
          </div>
        </>
      )}
    </div>
  );
}

/** The drawer's contents — everything that can fail. Split out so the strip above survives a crash. */
function EmmaDrawerBody({openCases, dropped, onClose,composePrompt}: {openCases: number; dropped: boolean; onClose: () => void;composePrompt:string}) {
  const {t} = useT();
  const {pathname} = useLocation();
  const {open, decide} = useCases();
  const messages = useDeskDrafts();
  const prepared = unreviewedDrafts(messages.data??[],open);

  const [tab, setTab] = useState<'chat' | 'waiting'>('chat');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  useEffect(()=>{if(composePrompt){setDraft(composePrompt);setTab('chat');}},[composePrompt]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(() => getEmmaSession());
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [historyFailure, setHistoryFailure] = useState<{status: number; message: string; expired: boolean} | null>(null);
  /** How many turns her store returned for the open conversation — so an empty box can say so. */
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const [asking, setAsking] = useState(false);
  /** ER-08: files on this conversation that no message has carried yet — the tray above the composer. */
  const [pending, setPending] = useState<Attachment[]>([]);
  /** How many files the conversation holds in all, so the 13th is refused before it is read. */
  const [attachedCount, setAttachedCount] = useState(0);
  const [uploading, setUploading] = useState<string | null>(null);
  /** A refusal or an upload failure, shown in the tray in words — never swallowed. */
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  /** Which conversation is on screen right now, for async reads that may land out of order. Kept in
   *  step by an effect rather than by hand, so every path that adopts an id is covered. */
  const liveSession = useRef<string | null>(session);
  useEffect(() => { liveSession.current = session; }, [session]);
  /**
   * THE CONVERSATION HE JUST ASKED FOR, WHICH DOES NOT EXIST IN HER STORE YET.
   *
   * Jacky: *"losing the new conversation i created, had 2 turns in it, but cant see it anymore"* —
   * and worse than losing it, the messages went into the PREVIOUS conversation. The reconcile below
   * exists for a stale id that names nothing; a brand-new conversation is an id that names nothing
   * *on purpose*. Without this flag the two fought and the repair won: pressing New minted an id,
   * `refresh` immediately replaced it with the newest existing conversation, and the next thing said
   * landed there.
   *
   * Held in sessionStorage as well as a ref, so a reload or a closed drawer does not turn a fresh
   * conversation into a stale id to be repaired.
   */
  const NEW_INTENT_KEY = `${HISTORY_KEY}.pending-new`;
  // A REF, not state: `refresh` is memoised on `[t]`, so anything it reads from state is frozen at
  // the value it had when the memo was made — the first version of this fix read `false` forever and
  // changed nothing. sessionStorage carries it across a reload or a closed drawer.
  const pendingNewRef = useRef<boolean>(false);
  try { pendingNewRef.current = sessionStorage.getItem(NEW_INTENT_KEY) === (getEmmaSession() ?? ''); } catch { /* private mode */ }

  // The tab badge counts what is waiting on him; the strip above already said it in one line.
  const waiting = open.length + prepared.length;

  const titleOf = (id: string | null): string => {
    if (!id) return t('emma.untitled');
    const hit = conversations.find((c) => c.id === id);
    if (hit) return hit.title;
    // A conversation this browser knows about but the list has not loaded yet — the first words of
    // what was said here, so the branch notice never says "(untitled)" about something he just read.
    // The line that crashed the drawer: `first.text` was not a string, so `.split` threw and the
    // boundary caught a white screen. A title is a nicety — it must never be able to take the
    // drawer with it.
    const first = turns.find((x) => x.role === 'you' && typeof x.text === 'string' && x.text.trim());
    return first ? (first.text.split('\n')[0] ?? '').slice(0, 60) || t('emma.untitled') : t('emma.untitled');
  };

  useEffect(() => { scroller.current?.scrollTo({top: scroller.current.scrollHeight}); }, [turns, dropped]);

  /** Re-read her store: the conversation list, and the active thread.
   *
   *  THE HISTORY READ IS THE THING THAT WAS BROKEN. `/emma-history/` is behind `requireAuth` on her
   *  API and this fetch sent no Authorization header, so it answered 401 and the catch below — which
   *  exists so a history failure never breaks the chat — silently swallowed it. The thread looked
   *  empty every time, which is why two turns of ONE conversation read as two conversations.
   *  `authHeaders()` is what every other hand-rolled fetch in this app already sends. */
  const refresh = useMemo(() => async (id: string | null) => {
    /** A NEW BEARER, once, when the server says the old one is dead. See `authedJson`. */
    const refreshAuth = async (): Promise<Record<string, string> | null> => {
      const c = supabase();
      if (!c) return null;
      const { data } = await c.auth.refreshSession();
      return data.session?.access_token ? {authorization: `Bearer ${data.session.access_token}`} : null;
    };
    const headers = await authHeaders().catch(() => ({}) as Record<string, string>);
    /**
     * THE STORED ID IS VERIFIED AGAINST HER LIST BEFORE IT IS READ.
     *
     * Jacky, looking at an empty thread with the conversation list full beside it: *"this chat box is
     * the empty i am talking about"*. Measured against production: reading the real id returns **4
     * messages**, while reading a stale or partial id returns **200 with 0 messages** — the server is
     * right in both cases, and the client rendered the second as "nothing said yet". Nothing ever
     * checked that the id in localStorage was a conversation that exists.
     *
     * So: if the stored id is not in her list, open the most recent conversation THAT HAS TURNS. Only
     * when she has no conversation with any turns does the drawer start a fresh one — and then the
     * empty box is the truth rather than a stale pointer.
     */
    let list: Conversation[] = [];
    try {
      list = await loadConversations(headers, 30, refreshAuth);
      setConversations(list);
      setListError(null);
    } catch (e) {
      setListError(e instanceof Error ? e.message : t('emma.unreachable'));
    }
    let target = id;
    // A deliberate new conversation is NOT a stale id: it is simply not in her list yet.
    const deliberate = pendingNewRef.current && !!id;
    if (!deliberate && list.length && (!id || !list.some((c) => c.id === id))) {
      target = list.find((c) => c.turnCount > 0)?.id ?? list[0]!.id;
      liveSession.current = target;
      setEmmaSession(target);
      setSession(target);
      writeHistory([target, ...readHistory().filter((x) => x !== target)]);
    }
    if (!target) return;
    setHistoryFailure(null);
    try {
      const past = await loadEmmaHistory(target, headers, 60, refreshAuth);
      // She knows it now — it is a real conversation, not an intention.
      if (past.length) endNewIntent();
      // The conversation may have moved while this was in flight — showing one thread's turns under
      // another thread's title is the same class of confusion this whole change exists to end.
      if (liveSession.current !== target) return;
      setLoadedCount(past.length);
      setTurns(past.flatMap((turn) => {
        // `text` is the replay shape's copy of whichever side said it; `message`/`reply` is the turn
        // shape's. Reading BOTH here is what stops one conversation from rendering half-empty — the
        // server has always sent `{role, text}`, and the client spent weeks believing `{message,
        // reply}` because a type cast said so.
        const question = turn.message || (turn.role === 'user' ? (turn.text ?? '') : '');
        const answer = turn.reply || (turn.role === 'emma' ? (turn.text ?? '') : '');
        return [
          {role: 'you' as const, text: question, ...(turn.attachments?.length ? {files: turn.attachments} : {})},
          {role: 'emma' as const, text: answer, model: turn.model ?? undefined},
        ];
      }));
      setHistoryFailure(null);
      // ER-08: the files bound to this conversation, from HER list — a file attached and never handed in
      // (the page closed first) is offered again in the tray rather than lost. Fails soft: the thread
      // is on screen already and a list that cannot load must not take it away.
      try {
        const files = await listAttachments(target, headers, refreshAuth);
        if (liveSession.current !== target) return;
        setAttachedCount(files.length);
        setPending(notYetHanded(files, past.map((turn) => turn.attachments ?? [])));
      } catch { /* the chat works without the tray */ }
    } catch (e) {
      // NOT SILENT ANY MORE. This catch is why tonight's failure stayed invisible: the read failed,
      // the thread kept whatever optimistic bubbles were in state — `working…` with an empty reply —
      // and nothing on screen distinguished that from "she is thinking". Jacky's screenshot of six
      // `working…` turns was a failed read wearing a loading state. The reason goes on screen now,
      // and a 401 says the one thing a person can act on.
      const status = e instanceof EmmaReadError ? e.status : 0;
      setHistoryFailure({
        status,
        message: e instanceof Error ? e.message : t('emma.unreachable'),
        expired: status === 401 || status === 403,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    if (!dropped) return;
    let active = true;
    void (async () => { if (active) await refresh(session); })();
    return () => { active = false; };
    // Re-read when he opens the drawer and when the conversation changes — NOT on every turn, because
    // the live thread is already on screen and a reload mid-turn would flicker it away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropped, session]);

  /** The conversation that was just created now exists in her store; stop calling it new. */
  function endNewIntent(): void {
    pendingNewRef.current = false;
    try { sessionStorage.removeItem(NEW_INTENT_KEY); } catch { /* private mode */ }
  }

  /** Adopt a conversation id everywhere it is remembered. */
  function adopt(id: string, remember = true): void {
    liveSession.current = id;
    setEmmaSession(id);
    setSession(id);
    if (remember) writeHistory([id, ...readHistory().filter((x) => x !== id)]);
  }

  /** Open a conversation from the list, or start a fresh one off the current thread. */
  async function openConversation(id: string): Promise<void> {
    adopt(id);
    setNotice(null);
    setFailure(null);
    setTurns([]);
    setPending([]);
    setAttachedCount(0);
    setFileNotice(null);
    await refresh(id);
  }

  /** New conversation — the operator's own way to move on without losing the thread he was in. */
  async function startNewConversation(): Promise<void> {
    const id = crypto.randomUUID();
    const parent = session;
    adopt(id);
    setEmmaSession(id);
    pendingNewRef.current = true;
    try { sessionStorage.setItem(NEW_INTENT_KEY, id); } catch { /* private mode */ }
    if (parent) {
      // The lineage travels with the first ask, so the new conversation can say what it came from and
      // carry the context — the server records `inherits_from` when it registers this id.
      try { sessionStorage.setItem(`${HISTORY_KEY}.parent`, parent); } catch { /* private mode */ }
    }
    setTurns([]);
    setPending([]);
    setAttachedCount(0);
    setFileNotice(null);
    setNotice(parent ? t('emma.branchCarried', {title: titleOf(parent)}) : null);
    setAsking(false);
    setFailure(null);
    void refresh(id);
  }

  /**
   * ER-08: hand her one file. Refused here in words when the Brain would refuse it (type, size, the
   * 13th on one conversation), otherwise uploaded against the conversation on screen and shown in the
   * tray until the next message carries it. The server's own refusal is shown as it came.
   *
   * A file needs a conversation to be bound to. The very first ask of a fresh browser has none yet (the
   * server mints one on the first turn), so the drawer mints it here — as a deliberate new conversation,
   * so `refresh` does not "repair" it into the newest existing one before the message is sent.
   */
  async function attach(file: File): Promise<void> {
    setFileNotice(null);
    const refusal = refusalFor(file, attachedCount);
    if (refusal) {
      setFileNotice(
        refusal.code === 'type' ? t(refusal.heic ? 'emma.attachRefuse.heic' : 'emma.attachRefuse.type', {mime: refusal.mime || t('emma.attachUnknownType')})
        : refusal.code === 'big' ? t('emma.attachRefuse.big', {mb: refusal.mb, max: refusal.max})
        : refusal.code === 'many' ? t('emma.attachRefuse.many', {max: refusal.max})
        : t('emma.attachRefuse.empty'));
      return;
    }
    let target = session;
    if (!target) {
      target = crypto.randomUUID();
      adopt(target);
      pendingNewRef.current = true;
      try { sessionStorage.setItem(NEW_INTENT_KEY, target); } catch { /* private mode */ }
    }
    const where = pageContext(pathname);
    setUploading(file.name);
    try {
      const headers = await authHeaders();
      const refreshAuth = async () => {
        const c = supabase();
        const data = c ? (await c.auth.refreshSession()).data : null;
        return data?.session?.access_token ? {authorization: `Bearer ${data.session.access_token}`} : null;
      };
      // The job on screen scopes the stored file to that job; anywhere else it belongs to the conversation.
      const a = await uploadAttachment(target, file, headers, where.kind === 'jobs' ? where.ref : null, refreshAuth);
      if (liveSession.current !== target) return;
      setPending((p) => (p.some((x) => x.revision_id === a.revision_id) ? p : [...p, a]));
      setAttachedCount((n) => n + 1);
      setTab('chat');
    } catch (e) {
      setFileNotice(e instanceof Error ? e.message : t('emma.unreachable'));
    } finally {
      setUploading(null);
    }
  }

  /** One turn. His instruction is the approval, so nothing here asks twice. */
  async function send(override?: string) {
    const message = (override ?? draft).trim();
    if (!message || busy) return;
    const where = pageContext(pathname);
    const sentIn = session;
    // ER-08: the tray's files go with THIS message. The Brain already reads their contents every turn;
    // the ids only record which turn they came with, so a reopened conversation shows them here.
    const files: TurnFile[] = pending.map(({revision_id, name, mime, size_bytes}) => ({revision_id, name, mime, size_bytes}));
    const reoffer = () => { if (files.length) setPending((p) => (p.length ? p : files.map((f) => ({id: f.revision_id, ...f})))); };
    setPending([]);
    setFileNotice(null);
    setDraft('');
    setFailure(null);
    setNotice(null);
    setBusy(true);
    setTurns((all) => [...all, {role: 'you', text: message, ...(files.length ? {files} : {})}, {role: 'emma', text: '', steps: [], progress: [], reasoning: [], live: true}]);
    const patch = (fn: (turn: Turn) => Turn) => setTurns((all) => all.map((turn, i) => (i === all.length - 1 ? fn(turn) : turn)));
    try {
      const history = turns.slice(-8).map((turn) => ({role: turn.role === 'you' ? 'user' : 'assistant', content: turn.text}));
      // A conversation the operator opened a moment ago carries its parent for ONE ask, so the server
      // can record the lineage and hand her what was said there.
      let inheritsFrom: string | undefined;
      try {
        const parent = sessionStorage.getItem(`${HISTORY_KEY}.parent`);
        if (parent) { inheritsFrom = parent; sessionStorage.removeItem(`${HISTORY_KEY}.parent`); }
      } catch { /* private mode */ }
      const response = await fetch('/emma-chat/', {
        method: 'POST',
        headers: {...(await authHeaders()), 'content-type': 'application/json', accept: 'text/event-stream'},
        // The page he is on travels with the ask, so "this job" means the job on screen.
        // The session travels with the ask: without it the server writes the turn against a new one
        // each time and nothing can be read back (Jacky: "talk to emma historical chat doesnt exist").
        body: JSON.stringify({
          message: `${message}\n\n[on screen: ${where.kind}${where.ref ? ` ${where.ref}` : ''}]`,
          history, sessionId: session ?? undefined, inheritsFrom, surface: 'crm',
          ...(files.length ? {attachments: files.map((f) => f.revision_id)} : {}),
        }),
      });
      if (!response.ok || !response.body) {
        // A refusal in words (a file not attached to this conversation) is shown as the server said it.
        const said = (await response.json().catch(() => null)) as {error?: string} | null;
        throw Error(response.status === 401 ? t('emma.signIn') : said?.error || t('emma.unreachable'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // A TURN THAT NEVER SAID `done` NEVER ANSWERED. Two frames carry the end of a turn —
      // `done` with her words, `error` with the reason — and this loop used to read neither
      // `error` nor "the stream ended without either", so the bubble kept the `working…`
      // placeholder and the drawer looked like she was still thinking. Jacky, on the header
      // chat: "show Working... nothing happen". She had stopped 600 ms in; the screen said
      // otherwise, which is worse than an error, because it reads as her being slow.
      let answered = false;
      let stopped: string | null = null;
      for (;;) {
        const {value, done} = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});
        const {frames, rest} = splitFrames(buffer);
        buffer = rest;
        for (const frame of frames) {
          const event = parseChatFrame(frame);
          if (!event) continue;
          if (event.type === 'session') {
            // Sent FIRST now, before the model is called — so a slow or failed turn still tells us
            // which conversation it belonged to, and the next ask continues it.
            const id = String(event.sessionId ?? '');
            if (id) { setEmmaSession(id); setSession(id); writeHistory([id, ...readHistory().filter((x) => x !== id)]); }
            continue;
          }
          if (event.type === 'branched') {
            // She moved to a new subject mid-turn. The answer on screen belongs to the conversation it
            // was asked in; the NEXT ask opens the new one, carrying what was said here.
            const id = String(event.sessionId ?? '');
            if (id) {
              setNotice(t('emma.branchCarried', {title: titleOf(sentIn)}));
              adopt(id);
            }
            continue;
          }
          if (event.type === 'done') {
            const result = event.result;
            // Her store tells us which session this turn belongs to; keep it so the next open
            // (and the full desk) can read the conversation back.
            const created = (result as {sessionCreated?: string | null}).sessionCreated;
            if (created) setEmmaSession(created);
            answered = true;
            // `done` carries the reply that stands — the streamed words when they were already plain,
            // the rewritten ones when they were not.
            patch((turn) => ({...turn, live: false, text: result.reply?.trim() || t('emma.noWords'), proposals: result.proposals, model: result.model, progress: (turn.progress ?? []).map((l) => (l.state === 'doing' ? {...l, state: 'ok'} : l))}));
          } else if (event.type === 'error') {
            stopped = String(event.message ?? '').trim() || t('emma.unreachable');
          } else if (event.type === 'tool') {
            const name = String(event.name ?? (event as {tool?: string}).tool ?? '');
            patch((turn) => ({...turn, progress: applyProgress(turn.progress ?? [], event), steps: name && event.phase !== 'ok' && event.phase !== 'error' ? [...(turn.steps ?? []), name] : turn.steps}));
          } else if (event.type === 'status') {
            patch((turn) => ({...turn, progress: applyProgress(turn.progress ?? [], event)}));
          } else if (event.type === 'reasoning') {
            const delta = String(event.delta ?? '').trim();
            if (delta) patch((turn) => ({...turn, reasoning: [...(turn.reasoning ?? []), delta]}));
          } else if (event.type === 'reply') {
            patch((turn) => ({...turn, text: event.reset ? '' : turn.text + (event.delta ?? '')}));
          }
        }
      }
      if (!answered) {
        // The reason is shown as the server sent it (a model id, a refused tool) rather than
        // swallowed: a plain "try again" is what made the first hour of this invisible.
        const why = stopped ?? t('emma.unreachable');
        setFailure(why);
        patch((turn) => ({...turn, live: false, text: turn.text || t('emma.stopped', {reason: why})}));
        reoffer();
      }
    } catch (e) {
      setFailure(e instanceof Error ? e.message : t('emma.unreachable'));
      patch((turn) => ({...turn, live: false, text: turn.text || t('emma.unreachable')}));
      reoffer();
    } finally {
      setBusy(false);
      // The list, not the thread: her turn may have branched, and the pane's titles and recency must
      // match what she just did. Re-reading the THREAD here would flicker the answer off screen.
      void authHeaders().then((h) => loadConversations(h, 30)).then(setConversations).catch(() => {});
    }
  }

  /** Reopen a file from its chip; a failure is said in the tray, never swallowed. */
  function openFile(file: TurnFile): void {
    setFileNotice(null);
    void authHeaders().then((h) => openAttachment(file, h)).catch((e: unknown) => setFileNotice(t('emma.attachOpenFailed', {reason: e instanceof Error ? e.message : t('emma.unreachable')})));
  }

  /**
   * The left pane's list: the operator's CONVERSATIONS, newest first — not the turns of the one he
   * happens to be in.
   *
   * This is the difference between "history" and a conversation. The old pane re-listed the current
   * thread's turns and called them history: with one broken read behind it, that produced the exact
   * confusion Jacky reported — two turns of one conversation, presented as though they were two.
   * A conversation is the unit now, and her branched ones appear here the moment she makes them.
   */
  const conversationRows = useMemo(() => {
    const known = new Set(conversations.map((c) => c.id));
    const rows = [...conversations];
    // The conversation he is in must always be in the list, even in the second before the list loads.
    if (session && !known.has(session)) {
      rows.unshift({
        id: session, title: titleOf(session), surface: 'crm', anchorRef: null,
        turnCount: 0, lastTurnAt: null, createdAt: new Date().toISOString(),
        inheritsFrom: null, branchReason: null,
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, session, turns, t]);

  return (
        <div className="flex min-h-0 flex-1 flex-col border-t">
          <div className="flex shrink-0 flex-wrap items-center gap-1 border-b px-4 py-1">
            {(['chat', 'waiting'] as const).map((id) => (
              <button key={id} type="button" onClick={() => setTab(id)} aria-pressed={tab === id}
                className={cn('-mb-px border-b-2 px-3 py-1.5 text-sm', tab === id ? 'border-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
                {id === 'chat' ? t('emma.tabChat') : t('emma.tabWaiting')}
                {id === 'waiting' && waiting > 0 && <span className="ml-1.5 rounded-full bg-warning px-1.5 font-mono text-micro text-warning-foreground">{waiting}</span>}
              </button>
            ))}
            <Link to="/emma" onClick={onClose} className="ml-auto text-sm text-primary underline">{t('emma.desk')}</Link>
            {/* A real close, where the eye is — not only the word at the far end of the strip. */}
            <button type="button" onClick={onClose} className="ml-2 min-h-9 rounded-md border bg-card px-3 text-sm hover:bg-accent">
              ✕ <span className="capitalize">{t('emma.close')}</span>
            </button>
          </div>

          {tab === 'chat' && (
            // Full width, so the conversation gets a left pane of conversations and the thread beside
            // it — what Jacky asked for once the drawer stopped being a narrow column.
            <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] md:grid-cols-[240px_minmax(0,1fr)] md:grid-rows-1">
              <aside className="max-h-32 overflow-y-auto border-b px-3 py-3 md:max-h-none md:border-b-0 md:border-r">
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <h3 className="text-micro uppercase tracking-wider text-muted-foreground">{t('emma.history')}</h3>
                  <button type="button" className="text-sm text-primary underline"
                    onClick={() => (session ? setAsking(true) : void startNewConversation())}>
                    {t('emma.newThread')}
                  </button>
                </div>
                {asking && (
                  // ONE confirmation, only for the deliberate act, and it names what stays behind — a
                  // silent switch would look like the thread he was reading had been thrown away.
                  <div className="mb-2 rounded-md border bg-card p-2 text-sm">
                    <p>{t('emma.branchAsk', {title: titleOf(session)})}</p>
                    <div className="mt-2 flex gap-2">
                      <button type="button" className="min-h-9 rounded-md bg-primary px-3 text-sm text-primary-foreground" onClick={() => void startNewConversation()}>{t('emma.branchStart')}</button>
                      <button type="button" className="min-h-9 rounded-md border px-3 text-sm" onClick={() => setAsking(false)}>{t('emma.branchCancel')}</button>
                    </div>
                  </div>
                )}
                {conversationRows.length === 0 && <p className="text-sm text-muted-foreground">{t('emma.historyEmpty')}</p>}
                {listError && conversationRows.length > 0 && <p className="mb-1 text-micro text-warning">{listError}</p>}
                <ul className="space-y-1">
                  {conversationRows.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => void openConversation(c.id)} aria-current={c.id === session}
                        className={cn('w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent', c.id === session && 'bg-accent font-medium')}>
                        <span className="block truncate">{c.title}</span>
                        <span className="block font-mono text-micro text-muted-foreground">
                          {(c.lastTurnAt ?? c.createdAt)
                            ? new Date(c.lastTurnAt ?? c.createdAt).toLocaleDateString('en-AU', {day: '2-digit', month: 'short', timeZone: 'Australia/Sydney'})
                            : ''}
                          {c.turnCount > 0 ? ` · ${t('emma.turns', {count: c.turnCount})}` : ''}
                          {c.inheritsFrom ? ` · ${t('emma.branchMark')}` : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="flex min-h-0 flex-col">
              <div ref={scroller} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
                {turns.length === 0 && (
                  <div className="flex items-start gap-3">
                  <EmmaPlanPose />
                  <p className="text-sm text-muted-foreground">
                    {t('emma.empty')}
                    {/* WHICH conversation, and how many turns she returned for it. An empty box that
                        cannot say what it asked for is the bug this whole evening was made of. */}
                    {session && (
                      <span className="mt-1 block font-mono text-micro">
                        {t('emma.emptyWhich', {id: session.slice(0, 8), count: loadedCount ?? '…'})}
                      </span>
                    )}
                  </p>
                  </div>
                )}
                {turns.map((turn, i) => { const said = turn.role === 'emma' ? quickReplies(turn.text) : {text: turn.text, replies: [] as string[]}; return (
                  <div key={i} className={cn(turn.role === 'emma' && 'flex items-start gap-2')}>
                  {turn.role === 'emma' && <EmmaAvatar size={28} busy={busy && i === turns.length - 1 && !said.text} className="mt-0.5" />}
                  <div id={`emma-turn-${i}`} className={cn('rounded-lg px-3 py-2 text-sm', turn.role === 'you' ? 'ml-auto max-w-[70%] bg-primary text-primary-foreground' : 'min-w-0 flex-1 bg-muted')}>
                    {/* While she works: her process, one plain line per step, the open one in motion. Jacky, 11 Oct 2026:
                        "the process calling is hard for me to understand" — the code names and her raw thinking fold
                        under "Show details" once she has answered, never open by default. */}
                    {turn.live && turn.progress?.length ? <ProgressLines lines={turn.progress} live /> : null}
                    {said.text ? <p className="whitespace-pre-wrap break-words">{said.text}</p> : turn.live && turn.progress?.length ? null : <p className="text-muted-foreground">{t('emma.thinking')}</p>}
                    {turn.files?.length ? <div className="mt-1.5 flex flex-wrap gap-1">{turn.files.map((f) => <FileChip key={f.revision_id} file={f} onOpen={openFile} onYou />)}</div> : null}
                    {/* Jacky, 5 October 2026: her choices are buttons; a tap is his reply. Only on her latest message. */}
                    {said.replies.length > 0 && i === turns.length - 1 && !busy && <div className="mt-2 flex flex-wrap gap-1.5">
                      {said.replies.map((r) => <button key={r} type="button" className="rounded-full border border-primary/40 bg-card px-3 py-1 text-sm text-primary hover:bg-primary/10" onClick={() => void send(r)}>{r}</button>)}
                    </div>}
                    {/* "Very robotic": the tool steps no longer sit above every reply. They fold under one quiet line —
                        the plain lines first, the code names and raw thinking one fold deeper. */}
                    {!turn.live && (turn.progress?.length || turn.steps?.length || turn.reasoning?.length) ? (
                      <details className="mt-1 text-micro text-muted-foreground"><summary className="cursor-pointer select-none">{t('emma.howIDidIt')}</summary>
                        {turn.progress?.length ? <ProgressLines lines={turn.progress} /> : null}
                        {turn.steps?.length || turn.reasoning?.length ? (
                          <details className="mt-1"><summary className="cursor-pointer select-none">{t('emma.showDetails')}</summary>
                            {turn.steps?.length ? <div className="mt-1 flex flex-wrap gap-1">{turn.steps.map((step, j) => <span key={`${step}-${j}`} className="rounded border bg-card px-1.5 font-mono text-micro">{step}</span>)}</div> : null}
                            {turn.reasoning?.length ? <div className="mt-1 space-y-1 whitespace-pre-wrap break-words">{turn.reasoning.map((r, j) => <p key={j}>{r}</p>)}</div> : null}
                          </details>
                        ) : null}
                      </details>
                    ) : null}
                    {turn.proposals?.length ? (
                      <ul className="mt-2 space-y-1 border-t pt-2">
                        {turn.proposals.map((p, j) => <li key={p.id ?? j} className="text-sm"><Link className="text-primary underline" to="/emma">{p.title ?? t('emma.openProposal')}</Link></li>)}
                      </ul>
                    ) : null}
                  </div>
                  </div>
                ); })}
                {notice && <p role="status" className="rounded-md bg-primary/5 px-3 py-2 text-sm text-primary">{notice}</p>}
                {historyFailure && (
                  <div role="alert" className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm">
                    <p className="font-medium">
                      {historyFailure.expired ? t('emma.historyNoSession') : t('emma.historyFailed')}
                    </p>
                    <p className="mt-1 break-words font-mono text-micro text-muted-foreground">
                      {historyFailure.status ? `${historyFailure.status} · ` : ''}{historyFailure.message}
                    </p>
                    <button
                      type="button"
                      onClick={() => void refresh(session)}
                      className="mt-2 min-h-9 rounded-md border bg-card px-3 text-sm"
                    >
                      {t('emma.historyRetry')}
                    </button>
                  </div>
                )}
                {failure && <p role="alert" className="text-sm text-warning">{failure}</p>}
              </div>
              {/* ER-08: the tray — files on this conversation that the next message will carry, the one being
                  kept right now, and any refusal in words. Empty, it takes no room. */}
              {(pending.length > 0 || uploading || fileNotice) && (
                <div className="flex flex-wrap items-center gap-1.5 border-t px-4 py-1.5">
                  {pending.length > 0 && <span className="text-micro text-muted-foreground">{t('emma.attachPending')}</span>}
                  {pending.map((a) => <FileChip key={a.revision_id} file={a} note={a.read_note ?? null} onOpen={openFile} onRemove={() => setPending((p) => p.filter((x) => x.revision_id !== a.revision_id))} />)}
                  {uploading && <span className="inline-flex items-center gap-1.5 text-micro text-muted-foreground"><span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />{t('emma.attachKeeping', {name: uploading})}</span>}
                  {fileNotice && <p role="alert" className="basis-full text-sm text-warning">{fileNotice}</p>}
                </div>
              )}
              <div className="flex items-end gap-2 border-t px-4 py-2">
                <input ref={fileInput} type="file" accept={ACCEPT} className="hidden" tabIndex={-1}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void attach(f); }} />
                <button type="button" onClick={() => fileInput.current?.click()} disabled={busy || !!uploading} title={t('emma.attachTitle')}
                  className="min-h-11 rounded-md border bg-card px-3 text-sm disabled:opacity-50">
                  {t('emma.attach')}
                </button>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
                  rows={2}
                  placeholder={pending.length ? t('emma.placeholderFile') : t('emma.placeholder')}
                  className="min-h-11 flex-1 resize-y rounded-md border bg-background px-3 py-2 text-sm"
                />
                <button type="button" onClick={() => void send()} disabled={busy || draft.trim().length === 0}
                  className="min-h-11 rounded-md bg-primary px-4 text-sm text-primary-foreground disabled:opacity-50">
                  {busy ? t('emma.working') : t('emma.send')}
                </button>
              </div>
              </div>
            </div>
          )}

          {tab === 'waiting' && (
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
              {waiting === 0 && <p className="text-sm text-muted-foreground">{t('emma.nothingWaiting')}</p>}
              {open.map((c) => <CaseCard key={c.kase.id} kase={c.kase} state={c.state} busy={c.busy} onDecide={(d, x) => decide(c.kase.id, d, x)} />)}
              {prepared.map((m) => { const summary=communicationSummary(m); return (
                <Link key={String(m.id)} to={summary.href} className="block rounded-lg border bg-card p-3 text-sm">
                  <strong>{summary.recipient || t('communications.to')}{summary.subject ? ` · ${summary.subject}` : ''}</strong>
                  <p className="mt-1 text-muted-foreground">{summary.channel} · {summary.address}</p>
                  <p className="mt-1 text-primary">{t('emma.reviewDraft')}</p>
                </Link>
              ); })}
              {openCases === 0 && waiting > 0 && <p className="text-micro text-muted-foreground">{t('emma.queueNote')}</p>}
            </div>
          )}
        </div>
  );
}
